import { useTheme } from "@material-ui/core/styles";
import useTeamCapacityInAggregator from "capacity/useTeamCapacityInAggregator";
import { Chart } from "chart.js";
import { DashboardPeriod } from "dashboards/DashboardPeriod";
import {
  getEngineerRoleLabel,
  useEngineerRoles,
} from "engineers/useEngineerRoles";
import {
  AggregatedCapacity,
  AggregatedCapacityItem,
  TeamCapacityAggregatorType
} from "resource-planner-model";
import { createRef, useEffect, useState } from "react";
import useCapacity from "../../capacity/useCapacity";
import { DashboardCard } from "../../components/DashboardCard";
import { getColors } from "../../utils/colorGenerator";

function CapacityByRoleCardImpl({
  capacities,
}: {
  capacities: AggregatedCapacity<AggregatedCapacityItem>;
}) {
  const chartRef = createRef<HTMLCanvasElement>();
  const [chart, setChart] = useState(null as null | Chart);
  const theme = useTheme();
  const engineerRoles = useEngineerRoles(
    capacities.getItems().map((item) => item.engineer.role)
  );

  useEffect(() => {
    if (chart) {
      return;
    }
    const ref = chartRef?.current as HTMLCanvasElement;
    const newChart = new Chart(ref, {
      type: "doughnut",
      data: {
        datasets: [],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
          },
        },
      },
    });
    setChart(newChart as any);
  }, []);

  useEffect(() => {
    if (!chart) {
      return;
    }
    const capacityByRole = capacities.getItems().reduce((map, item) => {
      const name = item.engineer.role;
      if (!map.has(name)) {
        map.set(name, new AggregatedCapacity<AggregatedCapacityItem>());
      }
      map.get(name)!.add(item);
      return map;
    }, new Map<string, AggregatedCapacity<AggregatedCapacityItem>>());
    const data = Array.from(capacityByRole.values()).map(
      (c) => c.allocatedHours
    );
    const backgroundColor = getColors(theme, data.length);
    chart.data = {
      labels: Array.from(capacityByRole.keys()).map(
        (region) =>
          getEngineerRoleLabel(engineerRoles, region) +
          " (" +
          capacityByRole.get(region)?.allocatedHours.toLocaleString() +
          ")"
      ),
      datasets: [{ data, backgroundColor }],
    };
    chart.update();
  }, [theme, chart, capacities, engineerRoles]);

  return (
    <DashboardCard title="Capacity by role (hours)" height={300}>
      {{ body: <canvas ref={chartRef} /> }}
    </DashboardCard>
  );
}

export function TeamCapacityByRoleCard({
  year,
  month,
  teamId,
}: {
  year: number;
  month: number;
  teamId: string;
}) {
  const capacities = useTeamCapacityInAggregator(
    year,
    month,
    TeamCapacityAggregatorType.TEAM,
    teamId
  );
  return <CapacityByRoleCardImpl capacities={capacities} />;
}

export function CapacityByRoleCard({ period }: { period: DashboardPeriod }) {
  const capacities = useCapacity(period.year, period.month);
  return <CapacityByRoleCardImpl capacities={capacities} />;
}
